import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { AgentRole, AgentStatus, type Agent } from "@/types/team";
import { Edit, User } from "lucide-react";

interface AgentsListProps {
  agents: Agent[];
  getTeamName: (teamId: string) => string;
  onEditAgent: (agent: Agent) => void;
  onToggleStatus: (agentId: string) => void;
}

export default function AgentsList({
  agents,
  getTeamName,
  onEditAgent,
  onToggleStatus,
}: AgentsListProps) {
  const getInitials = (name: string) => {
    return name
      ?.split(" ")
      ?.map((n) => n?.[0])
      ?.join("")
      ?.toUpperCase()
      ?.slice(0, 2);
  };

  const getRoleBadges = (permissions: AgentRole[]) => {
    return permissions.map((role) => {
      if (role === AgentRole.APP_ADMIN) {
        return <Badge key={role} className="bg-purple-600">Administrador</Badge>;
      }
      return <Badge key={role} variant="secondary">Agente</Badge>;
    });
  };

  if (agents.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center p-8 bg-muted/30 rounded-lg">
        <User className="h-12 w-12 text-muted-foreground mb-4" />
        <p className="text-muted-foreground text-center">
          Nenhum agente encontrado
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {agents.map((agent) => (
        <Card
          key={agent._id}
          className={`p-4 ${agent.status === AgentStatus.ACTIVE ? "bg-muted/30" : ""}`}
        >
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Avatar className="h-10 w-10">
                {agent.profileImage && (
                  <AvatarImage src={agent.profileImage} alt={agent.name} />
                )}
                <AvatarFallback>{getInitials(agent.name)}</AvatarFallback>
              </Avatar>

              <div className="space-y-1">
                <div className="font-medium flex items-center gap-2">
                  {agent.name}
                  {agent.isBot && <Badge variant="outline">Bot</Badge>}
                  {agent.status === AgentStatus.INACTIVE && (
                    <Badge variant="outline" className="text-muted-foreground">
                      Inativo
                    </Badge>
                  )}
                </div>
                <div className="text-sm text-muted-foreground">
                  {agent.email}
                </div>
                {agent.phone && (
                  <div className="text-sm text-muted-foreground">
                    {agent.phone}
                  </div>
                )}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 mt-2 md:mt-0">
              <div className="flex flex-wrap gap-1 max-w-xs">
                {agent.teams && agent.teams.length > 0 ? (
                  agent.teams.map((teamId) => (
                    <Badge key={teamId} variant="outline">
                      {getTeamName(teamId)}
                    </Badge>
                  ))
                ) : (
                  <Badge variant="outline" className="text-muted-foreground">
                    Sem equipe
                  </Badge>
                )}
              </div>

              <div className="flex gap-1">
                {getRoleBadges(agent.permissions)}
              </div>

              <div className="flex items-center gap-1 ml-auto">
                <Switch
                  checked={agent.status === AgentStatus.ACTIVE}
                  onCheckedChange={() => onToggleStatus(agent._id)}
                  className="mr-2"
                />

                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => onEditAgent(agent)}
                >
                  <Edit className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
}
